import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useStore, Draft, WorkspaceTypeData, ApiError, useWorkspaceTypeMutation } from 'zens-store'
import { useRouter, useNumberParams } from 'zens-router'
import { LayoutHeader, NavigationDelete, WorkspaceTypeForm, WorkspaceTypeHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const WorkspaceTypeEdit = () => {
  const [errors, setErrors] = useState<Record<string, string>>()
  const router = useRouter()
  const store = useStore()
  const { message } = store
  const [confirmDelete, setConfirmDelete] = useState<Draft<WorkspaceTypeData>>()
  const [{ companyId, workspaceTypeId }] = useNumberParams()
  const { t } = useTranslation()
  const mutation = useWorkspaceTypeMutation()

  const save = async (draft: Draft<WorkspaceTypeData>) => {
    try {
      await mutation.update(draft)
      message.add({
        message: t('Workspace type was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`WORKSPACE: Workspace type failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Workspace type could not be updated')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    router.push({ pathname: `/workspacetypes`, params: { companyId } })
  }

  const remove = async () => {
    try {
      setErrors({})
      await mutation.delete(confirmDelete!)
      message.add({
        message: t('Workspace type was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`LOCATION: Workspace type deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspace type could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push({ pathname: `/workspacetypes`, params: { companyId } })
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton title breadcrumb />}>
        <WorkspaceTypeHeader companyId={companyId} workspaceTypeId={workspaceTypeId} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<WorkspaceTypeForm.Skeleton />}>
          <WorkspaceTypeForm
            companyId={companyId}
            workspaceTypeId={workspaceTypeId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Workspace type')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default WorkspaceTypeEdit
